import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api, { formatError, formatPaise } from '@/lib/api';
import { costBreakdown } from '@/lib/campaign';
import TopUpDialog from '@/components/TopUpDialog';

// Must match the backend (services/campaign_flow.py): 30 days in all, and Meta's minimum a day for the days left.
const MAX_DAYS = 30;
const FALLBACK_MIN_DAILY = 10000; // ₹100 a day without Meta; the real minimum comes from the server
const MAX_EXTRA = 5000000; // ₹50,000 at a time
const dateText = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

/** More budget and/or more days for a live or paused ad. The extra (with our fee and GST) is held
 * from the wallet now; the same Meta campaign keeps running, so it keeps what Meta has learned. */
export default function ExtendAdDialog({ campaign, open, onOpenChange, onDone }) {
  const days = campaign.duration_days || 0;
  const budget = campaign.budget_paise || 0;
  const daily = days ? Math.round(budget / days) : 0;
  const maxAddDays = Math.max(0, MAX_DAYS - days);
  const [addDays, setAddDays] = useState(0);
  const [addBudget, setAddBudget] = useState(0);
  const [budgetTouched, setBudgetTouched] = useState(false);
  const [balance, setBalance] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [minDaily, setMinDaily] = useState(FALLBACK_MIN_DAILY);

  const loadBalance = () => api.get('/api/wallet/balance')
    .then(({ data }) => { setBalance(data.balance_paise); setTestMode(!!data.test_mode); }).catch(() => {});
  useEffect(() => {
    if (!open) return;
    setAddDays(Math.min(3, maxAddDays)); setAddBudget(Math.min(3, maxAddDays) * daily || daily * 3);
    setBudgetTouched(false); loadBalance();
    api.get(`/api/campaigns/${campaign.id}/budget-minimum`).then(({ data }) => setMinDaily(data.min_daily_paise || FALLBACK_MIN_DAILY)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Until the budget is set by hand, more days come with the same daily budget.
  const changeDays = (d) => { setAddDays(d); if (!budgetTouched) setAddBudget(d * daily); };

  const newEnd = new Date(new Date(campaign.end_at).getTime() + addDays * 86400000);
  const newBudget = budget + addBudget;
  const spent = campaign.performance?.spend_used_paise || 0;
  const daysLeft = Math.max((newEnd - Date.now()) / 86400000, 1);
  const perDay = Math.round((newBudget - spent) / daysLeft);
  const extra = costBreakdown(addBudget);
  const hold = testMode ? 0 : extra.total;
  const problem = !addDays && !addBudget ? 'Add some budget or some days.'
    : perDay < minDaily ? `That's under ₹${minDaily / 100} a day for the days left, the least Meta accepts. Add some budget as well.` : '';
  const short = balance !== null && hold > balance ? hold - balance : 0;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/campaigns/${campaign.id}/extend`, { add_days: addDays, add_budget_paise: addBudget });
      toast.success(addDays ? `Your ad now runs until ${dateText(newEnd)}` : 'Budget added to your ad');
      onDone?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      if (err.response?.status === 402) setTopUpOpen(true);
    }
    setBusy(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="extend-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Add budget or days</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Your ad keeps running as it is, so Meta keeps everything it has learned about who responds.</p>
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-2 block">More days: {addDays}</Label>
              {maxAddDays > 0 ? (
                <Slider value={[addDays]} min={0} max={maxAddDays} step={1} onValueChange={([v]) => changeDays(v)} data-testid="extend-days" />
              ) : <p className="text-xs text-muted-foreground">This ad already runs for {MAX_DAYS} days, the most an ad can run.</p>}
              <p className="text-xs text-muted-foreground mt-1.5">Ends {dateText(newEnd)}{addDays ? ` (was ${dateText(new Date(campaign.end_at))})` : ''}</p>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">More budget: {formatPaise(addBudget)}</Label>
              <Slider value={[addBudget / 100]} min={0} max={MAX_EXTRA / 100} step={100}
                onValueChange={([v]) => { setAddBudget(v * 100); setBudgetTouched(true); }} data-testid="extend-budget" />
              <p className="text-xs text-muted-foreground mt-1.5">
                Total budget {formatPaise(newBudget)} · about {formatPaise(Math.max(perDay, 0))} a day for the days left
              </p>
            </div>
            <div className="rounded-xl bg-butter/60 border-2 border-ink/15 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Extra ad spend</span><span>{formatPaise(extra.spend)}</span></div>
              <div className="flex justify-between"><span>Fee + GST</span><span>{formatPaise(extra.fee + extra.gst)}</span></div>
              <div className="flex justify-between font-bold border-t-2 border-dashed border-ink/40 pt-1">
                <span>{testMode ? 'Held from your wallet (test mode: nothing)' : 'Held from your wallet now'}</span><span>{formatPaise(hold)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">Anything Meta doesn&apos;t spend comes back to your wallet when the ad ends.
                {balance !== null && ` Wallet: ${formatPaise(balance)}.`}</p>
            </div>
            {problem && <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="extend-problem">{problem}</p>}
            {short > 0 ? (
              <Button className="w-full gradient-bg h-11" onClick={() => setTopUpOpen(true)} data-testid="extend-top-up">
                <Plus className="w-4 h-4" /> Add {formatPaise(short)} to your wallet first
              </Button>
            ) : (
              <Button className="w-full gradient-bg h-11 !font-extrabold" disabled={busy || !!problem} onClick={submit} data-testid="extend-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />} Add to my ad
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} suggested={Math.max(Math.ceil(short / 10000) * 10000, 50000)}
        onPaid={(b) => setBalance(b)} />
    </>
  );
}
